import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  FormField,
  IconButton,
  Input,
  PrimaryButton,
  SecondaryButton,
  ChevronDownIcon,
  ChevronUpIcon,
  CameraIcon,
  ExternalLinkIcon,
  MinusIcon,
  PlusIcon,
  ShuffleIcon,
  XIcon,
} from "../../design-system";
import { borders, surfaces, textColors, typography, interactive } from "../../design-system/tokens";
import {
  fetchLandingDemoPublicSites,
  fetchLandingWebsites,
  rebuildSingleDemoPublicSite,
  saveLandingWebsites,
  screenshotDemoSites,
  searchLandingWebsiteContainers,
} from "../utils/adminApi";

const formatOwner = (c) => {
  const username = c?.owner_username ? `@${c.owner_username}` : "—";
  const email = c?.owner_email ? ` • ${c.owner_email}` : "";
  return `${username}${email}`;
};

const Badge = ({ variant = "neutral", children }) => {
  const styles =
    variant === "success"
      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border-green-200 dark:border-green-800"
      : variant === "warning"
        ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800"
        : variant === "danger"
          ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 border-red-200 dark:border-red-800"
          : `${surfaces.tertiary} ${textColors.primary} ${borders.default}`;

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border ${typography.caption} font-medium ${styles}`}>
      {children}
    </span>
  );
};

/** Convert a demo-site row into the shape expected by addToFeatured / featured list. */
const demoSiteAsContainer = (site) => ({
  container_id: site.container_id,
  name: site.name,
  domain: site.domain,
  owner_username: site.owner_username,
  owner_email: site.owner_email,
  public: site.public,
  has_published: site.is_published,
});

const LandingWebsitesAdminPage = () => {
  // Search filters (separate inputs as requested)
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [user, setUser] = useState("");

  // Data
  const [featured, setFeatured] = useState([]); // ordered list
  const [results, setResults] = useState([]); // search results

  // UI state
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingDemoSites, setLoadingDemoSites] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rebuildingDemoSites, setRebuildingDemoSites] = useState(false);
  const [screenshotting, setScreenshotting] = useState(false);
  const [screenshottingKeys, setScreenshottingKeys] = useState(new Set());
  const [rebuildingKeys, setRebuildingKeys] = useState(new Set());
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null); // number (ms) | null
  const [notice, setNotice] = useState(null); // { type, message }
  const [error, setError] = useState("");
  const [demoSitesError, setDemoSitesError] = useState("");
  const [demoSites, setDemoSites] = useState([]);

  const featuredIds = useMemo(() => new Set((featured || []).map((c) => String(c.container_id))), [featured]);
  const hydratedRef = useRef(false);
  const lastSavedIdsRef = useRef("[]"); // JSON string for stable compare
  const autosaveTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  const getFeaturedIdsKey = (list) => {
    const ids = (list || [])
      .map((c) => Number(c?.container_id))
      .filter((n) => Number.isFinite(n));
    return JSON.stringify(ids);
  };

  const loadFeatured = async () => {
    setLoadingFeatured(true);
    setError("");
    try {
      const data = await fetchLandingWebsites();
      const next = Array.isArray(data?.results) ? data.results : [];
      setFeatured(next);
      lastSavedIdsRef.current = getFeaturedIdsKey(next);
      setDirty(false);
      hydratedRef.current = true;
    } catch (e) {
      setError(e?.message || "Failed to load landing websites.");
    } finally {
      setLoadingFeatured(false);
    }
  };

  const runSearch = async () => {
    setLoadingSearch(true);
    setError("");
    try {
      const data = await searchLandingWebsiteContainers({
        name: (name || "").trim(),
        domain: (domain || "").trim(),
        user: (user || "").trim(),
        limit: 50,
      });
      setResults(Array.isArray(data?.results) ? data.results : []);
    } catch (e) {
      setError(e?.message || "Failed to search website containers.");
    } finally {
      setLoadingSearch(false);
    }
  };

  const loadDemoSites = async () => {
    setLoadingDemoSites(true);
    setDemoSitesError("");
    try {
      const data = await fetchLandingDemoPublicSites();
      setDemoSites(Array.isArray(data?.results) ? data.results : []);
    } catch (e) {
      setDemoSitesError(e?.message || "Failed to load demo public websites.");
    } finally {
      setLoadingDemoSites(false);
    }
  };

  useEffect(() => {
    // Load current featured list and also run an initial search (no filters -> recent containers)
    loadFeatured();
    runSearch();
    loadDemoSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const addToFeatured = (container) => {
    if (!container) return;
    const key = String(container.container_id);
    setFeatured((prev) => {
      const arr = [...(prev || [])];
      if (arr.some((c) => String(c?.container_id) === key)) return arr;
      return [...arr, container];
    });
  };

  const removeFromFeatured = (containerId) => {
    const key = String(containerId);
    setFeatured((prev) => (prev || []).filter((c) => String(c.container_id) !== key));
  };

  const moveFeatured = (containerId, direction) => {
    const key = String(containerId);
    setFeatured((prev) => {
      const arr = [...(prev || [])];
      const idx = arr.findIndex((c) => String(c.container_id) === key);
      if (idx < 0) return arr;
      const nextIdx = direction === "up" ? idx - 1 : idx + 1;
      if (nextIdx < 0 || nextIdx >= arr.length) return arr;
      const tmp = arr[idx];
      arr[idx] = arr[nextIdx];
      arr[nextIdx] = tmp;
      return arr;
    });
  };

  const handleSave = async ({ trigger = "manual" } = {}) => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    setSaving(true);
    if (trigger === "manual") setNotice(null);
    setError("");
    try {
      const ids = (featured || []).map((c) => c.container_id);
      await saveLandingWebsites(ids);
      lastSavedIdsRef.current = getFeaturedIdsKey(featured);
      setDirty(false);
      setLastSavedAt(Date.now());
      if (trigger === "manual") {
        setNotice({ type: "success", message: "Saved landing websites." });
        await loadFeatured();
        await runSearch();
      }
    } catch (e) {
      const msg = e?.message || "Failed to save landing websites.";
      setNotice({ type: "error", message: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleRebuildDemoSites = async () => {
    setRebuildingDemoSites(true);
    setNotice(null);
    setDemoSitesError("");

    const sites = [...demoSites];
    const allKeys = sites.map((s) => s.key || s.domain).filter(Boolean);

    // Mark every key as rebuilding so individual row spinners show
    setRebuildingKeys((prev) => {
      const next = new Set(prev);
      allKeys.forEach((k) => next.add(k));
      return next;
    });

    let succeededCount = 0;
    const failedEntries = [];

    try {
      for (let i = 0; i < sites.length; i += 1) {
        if (!isMountedRef.current) return;

        const site = sites[i];
        const siteKey = site.key || site.domain;
        if (!siteKey) continue;

        setNotice({
          type: "success",
          message: `Rebuilding demo websites… ${i + 1}/${sites.length} ("${site.name || siteKey}")`,
        });

        try {
          const data = await rebuildSingleDemoPublicSite(siteKey);
          if (!isMountedRef.current) return;
          if (Array.isArray(data?.results)) {
            setDemoSites(data.results);
          }
          succeededCount += 1;
        } catch (e) {
          const statusHint = e?.status ? ` (HTTP ${e.status})` : "";
          const serverDetail = e?.data?.error || e?.data?.detail || "";
          const base = e?.message || `Failed "${site.name || siteKey}"`;
          const detail =
            serverDetail && serverDetail !== base
              ? `${base}${statusHint} — ${serverDetail}`
              : `${base}${statusHint}`;
          failedEntries.push({ name: site.name || siteKey, detail });
        } finally {
          if (isMountedRef.current) {
            setRebuildingKeys((prev) => {
              const next = new Set(prev);
              next.delete(siteKey);
              return next;
            });
          }
        }
      }

      if (!isMountedRef.current) return;

      await loadDemoSites();
      if (!isMountedRef.current) return;
      await runSearch();
      if (!isMountedRef.current) return;

      const totalCount = succeededCount + failedEntries.length;
      if (failedEntries.length === 0) {
        setNotice({
          type: "success",
          message: `Rebuilt all ${succeededCount} demo website${succeededCount === 1 ? "" : "s"}.`,
        });
      } else {
        const failedNames = failedEntries.map((f) => f.name).join(", ");
        setNotice({
          type: "error",
          message: `Rebuilt ${succeededCount}/${totalCount}. Failed: ${failedNames}.`,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setRebuildingDemoSites(false);
        setRebuildingKeys(new Set());
      }
    }
  };

  const handleRebuildSingleSite = async (siteKey, siteName) => {
    setRebuildingKeys((prev) => new Set(prev).add(siteKey));
    setNotice(null);
    setDemoSitesError("");
    try {
      const data = await rebuildSingleDemoPublicSite(siteKey);
      if (!isMountedRef.current) return;
      const durationMs = data?.duration_ms;
      const durationHint = Number.isFinite(durationMs) ? ` (${(durationMs / 1000).toFixed(1)}s)` : "";
      setNotice({
        type: "success",
        message: `Rebuilt "${siteName || siteKey}"${durationHint}.`,
      });
      // Refresh demo sites with a separate GET (the POST response may carry
      // stale list data because list_demo_public_sites runs inside the same
      // @transaction.atomic before it commits).
      try {
        const freshData = await fetchLandingDemoPublicSites();
        if (!isMountedRef.current) return;
        setDemoSites(Array.isArray(freshData?.results) ? freshData.results : []);
      } catch (_) {
        // Swallow refresh errors — the rebuild itself succeeded.
      }
      if (!isMountedRef.current) return;
      await runSearch();
    } catch (e) {
      if (!isMountedRef.current) return;
      const statusHint = e?.status ? ` (HTTP ${e.status})` : "";
      const serverDetail = e?.data?.error || e?.data?.detail || "";
      const base = e?.message || `Failed to rebuild "${siteName || siteKey}".`;
      const fullMessage =
        serverDetail && serverDetail !== base
          ? `${base}${statusHint} — ${serverDetail}`
          : `${base}${statusHint}`;
      setNotice({ type: "error", message: fullMessage });
    } finally {
      if (isMountedRef.current) {
        setRebuildingKeys((prev) => {
          const next = new Set(prev);
          next.delete(siteKey);
          return next;
        });
      }
    }
  };

  // Track unsaved changes + autosave (debounced). This makes "Add" stick even if you forget to press Save.
  useEffect(() => {
    if (!hydratedRef.current) return;
    const nextKey = getFeaturedIdsKey(featured);
    const isDirty = nextKey !== lastSavedIdsRef.current;
    setDirty(isDirty);

    if (!isDirty) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      handleSave({ trigger: "autosave" });
    }, 900);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featured]);

  return (
    <div className={`min-h-screen ${surfaces.modernApp}`}>
      <div className="mx-auto max-w-7xl space-y-8 px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
          <div className="min-w-0">
            <h1 className={`${typography.h2} ${textColors.brightest}`}>Landing Websites</h1>
            <p className={`${typography.bodySmall} ${textColors.bright}`}>
              Select and order which website containers appear on the public landing page. They now render as live,
              zoomed-out previews on desktop and as a mobile-friendly stacked list on smaller screens. Only public
              containers can be saved. Unpublished selections are allowed (they just won’t render publicly until a
              version is published).
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end sm:gap-4">
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
              <SecondaryButton className="w-full sm:w-auto whitespace-nowrap" onClick={() => runSearch()} disabled={loadingSearch}>
                {loadingSearch ? "Searching..." : "Search"}
              </SecondaryButton>
              <SecondaryButton className="w-full sm:w-auto whitespace-nowrap" onClick={() => loadFeatured()} disabled={loadingFeatured}>
                {loadingFeatured ? "Refreshing..." : "Refresh Featured"}
              </SecondaryButton>
              <PrimaryButton
                className={`w-full sm:w-auto whitespace-nowrap ${interactive.primaryGradientBtn}`}
                onClick={() => handleSave({ trigger: "manual" })}
                disabled={saving || !dirty}
              >
                {saving ? "Saving..." : dirty ? "Save" : "Saved"}
              </PrimaryButton>
            </div>
            <div className={`${typography.caption} ${dirty ? textColors.bright : textColors.tertiary}`}>
              {saving ? "Saving…" : dirty ? "Unsaved changes" : lastSavedAt ? "All changes saved" : ""}
            </div>
          </div>
        </div>

        {notice && (
          <div
            className={`rounded-2xl border px-4 py-3 ${
              notice.type === "error"
                ? "border-red-300 text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800"
                : "border-green-300 text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-200 dark:border-green-800"
            }`}
          >
            {notice.message}
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-red-300 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-200 dark:border-red-800 px-4 py-3">
            {error}
          </div>
        )}

        <Card variant="glass">
          <CardHeader title="Search Website Containers" subtitle="Filter by name, domain, or user (username/email/name)." />
          <CardBody>
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <FormField label="Website name" htmlFor="landing-name">
                  <Input
                    id="landing-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. John Portfolio"
                  />
                </FormField>
                <FormField label="Domain" htmlFor="landing-domain">
                  <Input id="landing-domain" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="e.g. john" />
                </FormField>
                <FormField label="User" htmlFor="landing-user">
                  <Input id="landing-user" value={user} onChange={(e) => setUser(e.target.value)} placeholder="e.g. @john or john@email.com" />
                </FormField>
              </div>

              <div className="overflow-x-auto">
                <table className={`w-full text-left ${typography.bodySmall} ${textColors.secondary}`}>
                  <thead>
                    <tr className={`border-b ${borders.glass}`}>
                      <th className={`py-3 ${textColors.bright}`}>Website</th>
                      <th className={`py-3 ${textColors.bright}`}>Domain</th>
                      <th className={`py-3 ${textColors.bright}`}>Owner</th>
                      <th className={`py-3 ${textColors.bright}`}>Public</th>
                      <th className={`py-3 ${textColors.bright}`}>Published</th>
                      <th className={`py-3 ${textColors.bright}`}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingSearch ? (
                      <tr>
                        <td className="py-4" colSpan={6}>
                          <span className={`${textColors.secondary}`}>Searching…</span>
                        </td>
                      </tr>
                    ) : (results || []).length === 0 ? (
                      <tr>
                        <td className="py-4" colSpan={6}>
                          <span className={`${textColors.secondary}`}>No results.</span>
                        </td>
                      </tr>
                    ) : (
                      results.map((c) => {
                        const isFeatured = featuredIds.has(String(c.container_id));
                        const canAdd = Boolean(c.public) && !isFeatured;
                        return (
                          <tr key={c.container_id} className={`border-b ${borders.glass}`}>
                            <td className="py-3">
                              <div className={`${textColors.bright} font-medium`}>{c.name || "—"}</div>
                              <div className={`${textColors.tertiary} text-xs`}>#{c.container_id}</div>
                            </td>
                            <td className={`py-3 ${textColors.secondary}`}>
                              <span className="inline-flex items-center gap-1.5">
                                {c.domain || "—"}
                                {c.domain && c.owner_username && (
                                  <a
                                    href={`/${c.owner_username}/${c.domain}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`Open /${c.owner_username}/${c.domain}`}
                                    className="inline-flex opacity-60 hover:opacity-100 transition-opacity"
                                  >
                                    <ExternalLinkIcon size="xs" />
                                  </a>
                                )}
                              </span>
                            </td>
                            <td className={`py-3 ${textColors.secondary}`}>{formatOwner(c)}</td>
                            <td className="py-3">
                              {c.public ? <Badge variant="success">Public</Badge> : <Badge variant="danger">Not public</Badge>}
                            </td>
                            <td className="py-3">
                              {c.has_published ? (
                                <Badge variant="success">Published</Badge>
                              ) : (
                                <Badge variant="warning">Not published</Badge>
                              )}
                            </td>
                            <td className="py-3">
                              {isFeatured ? (
                                <IconButton
                                  icon={<MinusIcon size="sm" />}
                                  onClick={() => removeFromFeatured(c.container_id)}
                                  variant="secondary"
                                  size="sm"
                                  shape="square"
                                  aria-label="Remove from featured"
                                  title="Remove from featured"
                                />
                              ) : (
                                <IconButton
                                  icon={<PlusIcon size="sm" />}
                                  onClick={() => addToFeatured(c)}
                                  disabled={!canAdd}
                                  variant="secondary"
                                  size="sm"
                                  shape="square"
                                  aria-label="Add to featured containers"
                                  title={canAdd ? "Add to featured" : c.public ? "Already selected" : "Only public containers can be added"}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card variant="glass">
          <CardHeader
            title="Selected (Featured) Containers"
            subtitle="Order is preserved. All selected containers appear in a continuously scrolling carousel on the landing page."
          />
          <CardBody>
            <div className="space-y-5">
              {loadingFeatured ? (
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>Loading…</div>
              ) : featured.length === 0 ? (
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>No featured containers selected.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className={`w-full text-left ${typography.bodySmall} ${textColors.secondary}`}>
                    <thead>
                      <tr className={`border-b ${borders.glass}`}>
                        <th className={`py-3 ${textColors.bright}`}>Order</th>
                        <th className={`py-3 ${textColors.bright}`}>Website</th>
                        <th className={`py-3 ${textColors.bright}`}>Domain</th>
                        <th className={`py-3 ${textColors.bright}`}>Owner</th>
                        <th className={`py-3 ${textColors.bright}`}>Public</th>
                        <th className={`py-3 ${textColors.bright}`}>Published</th>
                        <th className={`py-3 ${textColors.bright}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {featured.map((c, idx) => (
                        <tr key={c.container_id} className={`border-b ${borders.glass}`}>
                          <td className={`py-3 ${textColors.bright}`}>{idx + 1}</td>
                          <td className="py-3">
                            <div className={`${textColors.bright} font-medium`}>{c.name || "—"}</div>
                            <div className={`${textColors.tertiary} text-xs`}>#{c.container_id}</div>
                          </td>
                          <td className={`py-3 ${textColors.secondary}`}>
                            <span className="inline-flex items-center gap-1.5">
                              {c.domain || "—"}
                              {c.domain && c.owner_username && (
                                <a
                                  href={`/${c.owner_username}/${c.domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`Open /${c.owner_username}/${c.domain}`}
                                  className="inline-flex opacity-60 hover:opacity-100 transition-opacity"
                                >
                                  <ExternalLinkIcon size="xs" />
                                </a>
                              )}
                            </span>
                          </td>
                          <td className={`py-3 ${textColors.secondary}`}>{formatOwner(c)}</td>
                          <td className="py-3">
                            {c.public ? <Badge variant="success">Public</Badge> : <Badge variant="danger">Not public</Badge>}
                          </td>
                          <td className="py-3">
                            {c.has_published ? (
                              <Badge variant="success">Published</Badge>
                            ) : (
                              <Badge variant="warning">Not published</Badge>
                            )}
                          </td>
                          <td className="py-3">
                            <div className="flex flex-wrap items-center gap-3">
                              <IconButton
                                icon={<ChevronUpIcon size="sm" />}
                                onClick={() => moveFeatured(c.container_id, "up")}
                                disabled={idx === 0}
                                variant="secondary"
                                size="sm"
                                shape="square"
                                aria-label="Move featured container up"
                                title="Move up"
                              />
                              <IconButton
                                icon={<ChevronDownIcon size="sm" />}
                                onClick={() => moveFeatured(c.container_id, "down")}
                                disabled={idx === featured.length - 1}
                                variant="secondary"
                                size="sm"
                                shape="square"
                                aria-label="Move featured container down"
                                title="Move down"
                              />
                              <IconButton
                                icon={<XIcon size="sm" />}
                                onClick={() => removeFromFeatured(c.container_id)}
                                variant="secondary"
                                size="sm"
                                shape="square"
                                aria-label="Remove featured container"
                                title="Remove"
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        <Card variant="glass">
          <CardHeader
            title="Demo Public Websites"
            subtitle="Rebuild deterministic public demo websites from code. They remain public and searchable/selectable above, but they are not automatically featured on the landing page."
            actions={
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <SecondaryButton
                  className="w-full sm:w-auto whitespace-nowrap"
                  disabled={screenshotting}
                  onClick={async () => {
                    setScreenshotting(true);
                    setNotice({
                      type: "success",
                      message: "Screenshotting all demo sites (desktop + mobile)… this takes about a minute.",
                    });
                    try {
                      const data = await screenshotDemoSites();
                      if (!isMountedRef.current) return;
                      setNotice({ type: "success", message: data?.message || "Screenshots generated." });
                      await loadDemoSites();
                    } catch (e) {
                      if (!isMountedRef.current) return;
                      setNotice({ type: "error", message: e?.message || "Failed to generate screenshots." });
                    } finally {
                      if (isMountedRef.current) setScreenshotting(false);
                    }
                  }}
                  title="Generate desktop and mobile preview PNGs (local dev only)"
                >
                  {screenshotting ? "Screenshotting..." : "Screenshot Demos"}
                </SecondaryButton>
                <SecondaryButton
                  className="w-full sm:w-auto whitespace-nowrap"
                  onClick={handleRebuildDemoSites}
                  disabled={rebuildingDemoSites}
                >
                  {rebuildingDemoSites ? "Rebuilding..." : "Rebuild Demo Websites"}
                </SecondaryButton>
              </div>
            }
            className="flex-col gap-3 sm:flex-row sm:items-start"
          />
          <CardBody>
            <div className="space-y-5">
              {demoSitesError && (
                <div className="rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                  {demoSitesError}
                </div>
              )}

              {loadingDemoSites ? (
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>Loading demo public websites…</div>
              ) : demoSites.length === 0 ? (
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>No demo public websites configured.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className={`w-full text-left ${typography.bodySmall} ${textColors.secondary}`}>
                    <thead>
                      <tr className={`border-b ${borders.glass}`}>
                        <th className={`py-3 ${textColors.bright}`}>Website</th>
                        <th className={`py-3 ${textColors.bright}`}>Domain</th>
                        <th className={`py-3 ${textColors.bright}`}>Template</th>
                        <th className={`py-3 ${textColors.bright}`}>Style</th>
                        <th className={`py-3 ${textColors.bright}`}>Public</th>
                        <th className={`py-3 ${textColors.bright}`}>Published</th>
                        <th className={`py-3 ${textColors.bright}`}>Desktop / mobile</th>
                        <th className={`py-3 ${textColors.bright}`}>Landing</th>
                        <th className={`py-3 ${textColors.bright}`}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demoSites.map((site) => {
                        const siteKey = site.key || site.domain;
                        const isRebuilding = rebuildingKeys.has(siteKey);
                        const isFeaturedOnLanding = site.container_id && featuredIds.has(String(site.container_id));
                        const canAddToFeatured = site.exists && site.public && !isFeaturedOnLanding;
                        return (
                        <tr key={siteKey} className={`border-b ${borders.glass}`}>
                          <td className="py-3">
                            <div className={`${textColors.bright} font-medium`}>{site.name || "—"}</div>
                            <div className={`${textColors.tertiary} text-xs`}>
                              {site.exists ? `#${site.container_id}` : "Missing - rebuild to create"}
                            </div>
                          </td>
                          <td className={`py-3 ${textColors.secondary}`}>
                            <span className="inline-flex items-center gap-1.5">
                              {site.domain || "—"}
                              {site.domain && site.owner_username && (
                                <a
                                  href={`/${site.owner_username}/${site.domain}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`Open /${site.owner_username}/${site.domain}`}
                                  className="inline-flex opacity-60 hover:opacity-100 transition-opacity"
                                >
                                  <ExternalLinkIcon size="xs" />
                                </a>
                              )}
                            </span>
                          </td>
                          <td className={`py-3 ${textColors.secondary}`}>{site.template || "—"}</td>
                          <td className={`py-3 ${textColors.secondary}`}>{site.style || "—"}</td>
                          <td className="py-3">
                            {site.public ? <Badge variant="success">Public</Badge> : <Badge variant="warning">Missing</Badge>}
                          </td>
                          <td className="py-3">
                            {site.is_published ? <Badge variant="success">Published</Badge> : <Badge variant="warning">Not published</Badge>}
                          </td>
                          <td className="py-3">
                            <div className="flex flex-col gap-1.5">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`${typography.caption} ${textColors.tertiary} shrink-0`}>Desktop</span>
                                {site.has_screenshot ? (
                                  <Badge variant="success">Available</Badge>
                                ) : (
                                  <Badge variant="warning">Missing</Badge>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`${typography.caption} ${textColors.tertiary} shrink-0`}>Mobile</span>
                                {site.has_screenshot_mobile ? (
                                  <Badge variant="success">Available</Badge>
                                ) : (
                                  <Badge variant="warning">Missing</Badge>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3">
                            {isFeaturedOnLanding ? <Badge variant="success">Selected</Badge> : <Badge>Selectable</Badge>}
                          </td>
                          <td className="py-3">
                            <div className="flex flex-wrap items-center gap-3">
                              <IconButton
                                icon={<ShuffleIcon size="sm" />}
                                onClick={() => handleRebuildSingleSite(siteKey, site.name)}
                                disabled={isRebuilding || rebuildingDemoSites}
                                variant="secondary"
                                size="sm"
                                shape="square"
                                aria-label={`Rebuild ${site.name || siteKey}`}
                                title={isRebuilding ? "Rebuilding…" : `Rebuild ${site.name || siteKey}`}
                              />
                              <IconButton
                                icon={<CameraIcon size="sm" />}
                                disabled={screenshottingKeys.has(siteKey) || screenshotting}
                                onClick={async () => {
                                  setScreenshottingKeys((prev) => new Set(prev).add(siteKey));
                                  setNotice({ type: "success", message: `Screenshotting "${site.name || siteKey}" (desktop + mobile)…` });
                                  try {
                                    const data = await screenshotDemoSites(site.domain);
                                    if (!isMountedRef.current) return;
                                    setNotice({ type: "success", message: data?.message || `Screenshot generated for "${site.name}".` });
                                    await loadDemoSites();
                                  } catch (e) {
                                    if (!isMountedRef.current) return;
                                    setNotice({ type: "error", message: e?.message || `Failed to screenshot "${site.name}".` });
                                  } finally {
                                    if (isMountedRef.current) {
                                      setScreenshottingKeys((prev) => {
                                        const next = new Set(prev);
                                        next.delete(siteKey);
                                        return next;
                                      });
                                    }
                                  }
                                }}
                                variant="secondary"
                                size="sm"
                                shape="square"
                                aria-label={`Screenshot ${site.name || siteKey}`}
                                title={
                                  screenshottingKeys.has(siteKey)
                                    ? "Screenshotting…"
                                    : `Screenshot ${site.name || siteKey} (desktop + mobile)`
                                }
                              />
                              {isFeaturedOnLanding ? (
                                <IconButton
                                  icon={<MinusIcon size="sm" />}
                                  onClick={() => removeFromFeatured(site.container_id)}
                                  variant="secondary"
                                  size="sm"
                                  shape="square"
                                  aria-label={`Remove "${site.name}" from featured`}
                                  title="Remove from featured"
                                />
                              ) : (
                                <IconButton
                                  icon={<PlusIcon size="sm" />}
                                  onClick={() => addToFeatured(demoSiteAsContainer(site))}
                                  disabled={!canAddToFeatured}
                                  variant="secondary"
                                  size="sm"
                                  shape="square"
                                  aria-label={`Add "${site.name}" to featured`}
                                  title={
                                    !site.exists
                                      ? "Rebuild first"
                                      : !site.public
                                        ? "Site not public"
                                        : "Add to featured"
                                  }
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default LandingWebsitesAdminPage;

